// client/src/pages/DiscoveryReport.js
// ENQ Entity Bank — screen 1b.
// Reads GET /api/projects/:id/discovery and renders the discovered entity
// surface (grouped by ENQ class, with salience + provenance) plus the top
// crawled pages. Lets the user confirm the surface, flipping the project
// from discovery_complete -> entities_confirmed.
//
// Renders as page content inside SidebarLayout (no sidebar/topbar of its own).
//
// Conventions matched from the existing codebase:
//   - projectId read from the route via useParams (same as CampaignRouter)
//   - auth via header { 'x-user-id': userId } (same as App.js fetchCampaigns)
//   - apiBaseUrl + userId passed in as props
//   - brand palette + Work Sans, matching SidebarLayout / CampaignScorecard
//
// NOTE: salience and entity_density arrive as STRINGS (Postgres NUMERIC), so we
// coerce with Number() before formatting. discovery_evidence is JSONB (object).
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// Brand colors (matches SidebarLayout)
const colors = {
  primary: '#39393a',   // Jet
  secondary: '#048ba8', // Blue Munsell
  tertiary: '#e9c46a',  // Saffron
  accent: '#f4a261',    // Sandy Brown
  grey: '#e76f51',      // Burnt Sienna
  green: '#01796f'      // Pine Green
};
// Friendly labels for known ENQ classes. The taxonomy expanded in migration 009,
// so anything not listed falls back to the raw class name — we never hardcode a
// fixed set, we render whatever classes come back.
const CLASS_LABELS = {
  BRAND: 'Brand',
  COMPETITOR: 'Competitors',
  SERVICE: 'Services',
  PRODUCT: 'Products',
  REGULATOR: 'Regulators',
  CONCEPT: 'Concepts',
  TECHNOLOGY: 'Technology',
  AUDIENCE: 'Audiences',
  GEOGRAPHY: 'Geography',
  CHANNEL: 'Channels',
  FORMAT: 'Formats',
  METRIC: 'Metrics',
  ROLE: 'Roles',
  EVENT: 'Events',
  INDUSTRY_TERM: 'Industry Terms',
  CUSTOMER_SEGMENT: 'Customer Segments',
  OTHER: 'Other'
};
// enq_status -> { label, color } for the header badge.
const STATUS_BADGE = {
  not_started: { label: 'Not started', color: colors.primary },
  discovery_pending: { label: 'Queued', color: colors.accent },
  discovery_running: { label: 'Discovery running', color: colors.accent },
  discovery_complete: { label: 'Ready to confirm', color: colors.secondary },
  discovery_failed: { label: 'Discovery failed', color: colors.grey },
  entities_confirmed: { label: 'Confirmed', color: colors.green }
};
const toNum = (v) => (v === null || v === undefined ? null : Number(v));
// Format a salience value (0..1 from NLP) to 3 dp; '—' if absent.
const fmtSalience = (v) => {
  const n = toNum(v);
  return n === null || Number.isNaN(n) ? '—' : n.toFixed(3);
};
// Provenance hint from discovery_evidence (a per-URL salience map). Defensive:
// only counts keys if it's a plain object with entries.
const evidenceSummary = (evidence) => {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return null;
  const n = Object.keys(evidence).length;
  return n > 0 ? `seen on ${n} page${n === 1 ? '' : 's'}` : null;
};
function DiscoveryReport({ apiBaseUrl, userId }) {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(null);
  useEffect(() => {
    if (projectId) fetchDiscovery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const fetchDiscovery = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/projects/${projectId}/discovery`, {
        headers: { 'x-user-id': userId }
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error || `Request failed (${res.status})`);
        setData(null);
      } else {
        setData(json);
      }
    } catch (err) {
      setError(err.message || 'Network error');
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmMsg(null);
    try {
      const res = await fetch(`${apiBaseUrl}/projects/${projectId}/confirm-entities`, {
        method: 'POST',
        headers: { 'x-user-id': userId }
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setConfirmMsg({ ok: false, text: json.error || `Confirm failed (${res.status})` });
      } else {
        setConfirmMsg({ ok: true, text: 'Entity surface confirmed.' });
        fetchDiscovery();
      }
    } catch (err) {
      setConfirmMsg({ ok: false, text: err.message || 'Network error' });
    } finally {
      setConfirming(false);
    }
  };
  const pageStyle = {
    padding: '24px',
    fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: colors.primary,
    maxWidth: '1100px'
  };
  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ color: '#6b7280', fontSize: '15px' }}>Loading discovery report…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{
          padding: '16px 20px',
          borderRadius: '10px',
          backgroundColor: '#fef2f2',
          border: `1px solid ${colors.grey}`,
          color: colors.grey,
          fontSize: '14px'
        }}>
          <strong>Couldn't load the discovery report.</strong>
          <div style={{ marginTop: '6px', color: '#7f1d1d' }}>{error}</div>
        </div>
      </div>
    );
  }
  const { project, surface, pages } = data;
  const badge = STATUS_BADGE[project.enq_status] || { label: project.enq_status, color: colors.primary };
  const classKeys = Object.keys(surface.byClass);
  const hasEntities = surface.counts.total > 0;
  return (
    <div style={pageStyle}>
      {/* ── Project header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0 }}>{project.name}</h1>
          <div style={{ marginTop: '6px', color: '#6b7280', fontSize: '14px' }}>
            {project.domain}
            {project.industry ? ` · ${project.industry}` : ''}
          </div>
        </div>
        <span style={{
          flexShrink: 0,
          fontSize: '12px',
          fontWeight: 600,
          color: 'white',
          backgroundColor: badge.color,
          padding: '6px 12px',
          borderRadius: '9999px',
          whiteSpace: 'nowrap'
        }}>
          {badge.label}
        </span>
      </div>
      <div style={{ marginTop: '8px', color: '#6b7280', fontSize: '13px' }}>
        {surface.counts.total} {surface.counts.total === 1 ? 'entity' : 'entities'} across {classKeys.length} {classKeys.length === 1 ? 'class' : 'classes'} · {pages.length} crawled {pages.length === 1 ? 'page' : 'pages'}
      </div>
      {/* ── Empty / in-progress states ── */}
      {!hasEntities && (
        <div style={{
          marginTop: '24px',
          padding: '20px',
          borderRadius: '10px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          {project.enq_status === 'discovery_running' || project.enq_status === 'discovery_pending'
            ? 'Discovery is still running. This page will show the entity surface once it completes.'
            : project.enq_status === 'discovery_failed'
              ? 'Discovery failed for this project. Re-run discovery, then refresh.'
              : 'No entities discovered yet.'}
        </div>
      )}
      {/* ── Entity surface, grouped by class ── */}
      {hasEntities && classKeys.map((cls) => {
        const entities = surface.byClass[cls];
        return (
          <div key={cls} style={{ marginTop: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.secondary, margin: 0 }}>
                {CLASS_LABELS[cls] || cls}
              </h2>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>{surface.counts.byClass[cls]}</span>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              {entities.map((e, i) => {
                const prov = evidenceSummary(e.discovery_evidence);
                return (
                  <div key={e.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '10px 16px',
                    backgroundColor: i % 2 === 0 ? 'white' : '#fafafa',
                    fontSize: '14px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.entity_name}
                      </span>
                      {e.confirmed_by_user && (
                        <span style={{ fontSize: '11px', color: colors.green, fontWeight: 600 }}>✓</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0, color: '#6b7280', fontSize: '12px' }}>
                      <span style={{
                        textTransform: 'capitalize',
                        backgroundColor: '#f3f4f6',
                        padding: '2px 8px',
                        borderRadius: '6px'
                      }}>
                        {e.source}
                      </span>
                      {prov && <span>{prov}</span>}
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: colors.primary }}>
                        {fmtSalience(e.salience)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {/* ── Top crawled pages ── */}
      {pages.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.secondary, marginBottom: '10px' }}>
            Top Crawled Pages
          </h2>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
            {pages.map((p, i) => (
              <div key={p.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '10px 16px',
                backgroundColor: i % 2 === 0 ? 'white' : '#fafafa',
                fontSize: '13px'
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.primary }}>
                  {p.url}
                </span>
                <span style={{ flexShrink: 0, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                  density {fmtSalience(p.entity_density)} · {p.entity_count} {p.entity_count === 1 ? 'entity' : 'entities'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* ── Confirm bar ── */}
      <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
        {project.enq_status === 'entities_confirmed' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ color: colors.green, fontWeight: 600, fontSize: '14px' }}>
              ✓ Entity surface confirmed.
            </div>
            <button
              onClick={() => { window.location.href = `/project/${projectId}/entity-bank-prompt-generator`; }}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 700,
                fontSize: '15px',
                color: 'white',
                backgroundColor: colors.secondary,
                cursor: 'pointer'
              }}
            >
              Build Campaign →
            </button>
          </div>
        ) : project.enq_status === 'discovery_complete' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={handleConfirm}
              disabled={confirming || !hasEntities}
              style={{
                padding: '12px 20px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 700,
                fontSize: '15px',
                color: 'white',
                backgroundColor: colors.grey,
                cursor: confirming || !hasEntities ? 'not-allowed' : 'pointer',
                opacity: confirming || !hasEntities ? 0.6 : 1
              }}
            >
              {confirming ? 'Confirming…' : 'Confirm Entity Bank'}
            </button>
            {confirmMsg && (
              <span style={{ fontSize: '13px', color: confirmMsg.ok ? colors.green : colors.grey }}>
                {confirmMsg.text}
              </span>
            )}
          </div>
        ) : (
          <div style={{ color: '#9ca3af', fontSize: '13px' }}>
            Confirmation becomes available once discovery completes.
          </div>
        )}
      </div>
    </div>
  );
}
export default DiscoveryReport;
